'use client';
import React, { useEffect, useRef } from 'react';
import { ApiAttribute } from '@context/openapi/types';
import InputFieldContainer from '@components/ui/DynamicForm/InputFieldContainer';
import InputLabel from '@components/ui/DynamicForm/InputLabel';
import Dropdown from '@components/ui/DynamicForm/Dropdown';
import InputField from '@components/ui/DynamicForm/InputField';
import DateRangeInput from '@components/ui/DynamicForm/DateRangeInput';
import NumberRangeInput from '@components/ui/DynamicForm/NumberRangeInput';
import InputDescription from '@components/ui/DynamicForm/InputDescription';
import MultiSelectDropdown from '@components/ui/DynamicForm/MultiSelectDropdown';
import { useExplorer } from '@/hooks/useExplorer';

interface FilterInputProps {
  field: ApiAttribute;
  onChange: (field: ApiAttribute, value: string) => void;
  onFilterChange?: (field: ApiAttribute, value: string) => void;
}

export const FilterInput: React.FC<FilterInputProps> = ({
  field,
  onChange,
  onFilterChange,
}) => {
  const [_isHelpVisible, setIsHelpVisible] = React.useState(false);
  const prevFilterRef = useRef<string | undefined>(field.filter);
  const prevIsDateRangeRef = useRef<boolean>(false);
  const { endpointAttributes } = useExplorer();

  const selectedOperator = field.filter
    ? field.child?.find((child) => {
        return (
          child.key === `${field.key}.${field.filter}` ||
          child.key === field.filter
        );
      })
    : null;

  const filterType = field.filterType;
  const filterOperator = field.filter?.toLowerCase() || '';

  const isArrayOperator = ['in', 'not_in', 'between'].includes(filterOperator);
  const filterValueType = isArrayOperator ? 'array' : 'single';

  const isDateRangeFilter =
    filterType === 'TimestampFilter' &&
    (filterValueType === 'array' || filterOperator === 'between');

  const isNumberRangeFilter =
    filterType === 'NumberFilter' &&
    (filterValueType === 'array' || filterOperator === 'between');

  const shouldUseMultiSelect =
    filterType === 'EnumFilter' &&
    filterValueType === 'array' &&
    endpointAttributes?.resourceEnums?.[field.name] !== undefined;

  const enumOptions = shouldUseMultiSelect
    ? (endpointAttributes?.resourceEnums?.[field.name] ?? [])
    : [];

  const shouldUseDropdown =
    !shouldUseMultiSelect &&
    selectedOperator?.type === 'ENUM' &&
    selectedOperator.child &&
    selectedOperator.child.length > 0;

  useEffect(() => {
    const prevFilter = prevFilterRef.current;
    const prevIsDateRange = prevIsDateRangeRef.current;

    if (
      prevFilter !== undefined &&
      prevFilter !== field.filter &&
      (filterType === 'TimestampFilter' || filterType === 'NumberFilter')
    ) {
      const wasDateRange = prevIsDateRange;
      const isNowDateRange =
        (filterType === 'TimestampFilter' && isDateRangeFilter) ||
        (filterType === 'NumberFilter' && isNumberRangeFilter);

      if (wasDateRange !== isNowDateRange) {
        onChange(field, '');
      }
    }

    prevFilterRef.current = field.filter;
    prevIsDateRangeRef.current =
      (filterType === 'TimestampFilter' && isDateRangeFilter) ||
      (filterType === 'NumberFilter' && isNumberRangeFilter);
  }, [
    field.filter,
    isDateRangeFilter,
    isNumberRangeFilter,
    filterType,
    onChange,
  ]);

  let valueField: ApiAttribute = field;
  if (shouldUseDropdown) {
    valueField = selectedOperator!;
  } else if (filterType === 'TimestampFilter' && !isDateRangeFilter) {
    valueField = {
      ...field,
      type: 'DATE',
    };
  }

  const handleValueChange = React.useCallback(
    (changedField: ApiAttribute, value: string) => {
      onChange(field, value);
    },
    [field, onChange]
  );

  const handleFilterChange = React.useCallback(
    (changedField: ApiAttribute, value: string) => {
      if (
        (filterType === 'TimestampFilter' || filterType === 'NumberFilter') &&
        field.filter !== value
      ) {
        onChange(field, '');
      }
      if (onFilterChange) {
        onFilterChange(changedField, value);
      }
    },
    [field.filter, filterType, onChange, onFilterChange]
  );

  return (
    <InputFieldContainer field_key={field.key}>
      <div className="flex w-full pl-2 bg-[#d3e4e6]">
        <InputLabel field={field} />
        <div className="ml-auto relative overflow-visible group flex items-center gap-1 flex-1 w-full min-w-0">
          <div className="flex justify-between w-full min-w-0">
            {/* Filter Operator Dropdown */}
            <div className="w-[35%] relative flex-shrink-0 min-w-0">
              <Dropdown
                field={field}
                value={field.filter ?? ''}
                onChange={handleFilterChange}
                placeholder="Select"
              />
            </div>
            {/* Filter Value Input/Dropdown */}
            <div className="flex-1 relative flex-shrink-0 min-w-0">
              {shouldUseMultiSelect ? (
                <MultiSelectDropdown
                  field={valueField}
                  value={(field.value as string) || ''}
                  onChange={handleValueChange}
                  options={enumOptions}
                  placeholder="Select values"
                  onHelpVisible={(state: boolean) => {
                    setIsHelpVisible(state);
                  }}
                  disabled={!selectedOperator}
                />
              ) : shouldUseDropdown ? (
                <div className="flex items-center w-full min-w-0 gap-2">
                  <Dropdown
                    field={valueField}
                    value={(field.value as string) || ''}
                    onChange={handleValueChange}
                    placeholder="Select a Value"
                    disabled={!selectedOperator}
                  />
                  <InputDescription
                    description={field.description}
                    onHelpVisible={(state: boolean) => {
                      setIsHelpVisible(state);
                    }}
                  />
                </div>
              ) : isDateRangeFilter ? (
                <DateRangeInput
                  field={valueField}
                  onChange={handleValueChange}
                  onHelpVisible={(state: boolean) => {
                    setIsHelpVisible(state);
                  }}
                  disabled={!selectedOperator}
                />
              ) : isNumberRangeFilter ? (
                <NumberRangeInput
                  field={valueField}
                  onChange={handleValueChange}
                  onHelpVisible={(state: boolean) => {
                    setIsHelpVisible(state);
                  }}
                  disabled={!selectedOperator}
                />
              ) : (
                <InputField
                  field={valueField}
                  onChange={handleValueChange}
                  onHelpVisible={(state: boolean) => {
                    setIsHelpVisible(state);
                  }}
                  isFilterValueInput={true}
                  disabled={!selectedOperator}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </InputFieldContainer>
  );
};

export default FilterInput;
